<template>
  <div class="plat-account-board mp-page mp-page--wide" :class="{ ready: loaded }">
    <header class="board-top">
      <BoardCrumb
        level="account"
        :platform="cfg.key"
        :group-id="fromGroupId"
        :group-name="fromGroupName"
        :range="days"
        :account-label="account.display_name || accountId"
      />
      <div class="board-actions">
        <el-button v-if="fromGroupId" plain @click="backToPlatGroup">返回{{ cfg.label }}分组</el-button>
        <div class="range-switch">
          <button
            v-for="d in cfg.ranges"
            :key="d"
            :class="{ on: days === d }"
            @click="setDays(d)"
          >{{ rangeLabel(d) }}</button>
        </div>
        <el-button :loading="syncing" type="primary" @click="syncBoard">同步数据</el-button>
        <el-button :loading="loading && !syncing" @click="loadBoard(false)">查看本地</el-button>
      </div>
    </header>

    <el-alert v-if="error" type="error" :closable="false" show-icon class="board-alert" :title="error" />
    <el-alert
      v-else-if="fromCache || dataSource === 'local'"
      type="warning"
      :closable="false"
      show-icon
      class="board-alert"
      :title="cacheTitle"
    />
    <el-alert
      v-if="statsError"
      type="info"
      :closable="false"
      show-icon
      class="board-alert"
      :title="`基础数据：${statsError}（总览仍可用）`"
    />

    <section class="hero" v-loading="loading && !loaded">
      <div class="hero-identity">
        <el-avatar :size="64" :src="account.avatar || account.avatar_url">{{ (account.display_name || '?')[0] }}</el-avatar>
        <div>
          <h1>{{ account.display_name || accountId }}</h1>
          <p>
            <span>{{ cfg.creatorLabel }}</span>
            <span v-if="account.creator_level != null"> · Lv {{ account.creator_level }}</span>
            <span v-if="account.level"> · {{ account.level }}</span>
            <span v-if="account.followers != null"> · 粉丝 {{ formatNum(account.followers) }}</span>
            <span v-if="account.external_user_id" class="mono"> · {{ account.external_user_id }}</span>
            <span v-if="fetchedAt"> · 更新 {{ formatTime(fetchedAt) }}</span>
          </p>
        </div>
      </div>
      <div class="hero-kpis">
        <div v-for="k in cfg.heroKpis" :key="k.label" class="hk">
          <span>{{ k.label }}</span>
          <strong>{{ formatNum(kpiValue(k)) }}</strong>
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="section-head">
        <h2>内容数据 · {{ periodLabel }}</h2>
        <el-radio-group v-if="cfg.showPortrait" v-model="mainTab" size="small">
          <el-radio-button value="detail">详细数据</el-radio-button>
          <el-radio-button value="advanced">高级数据</el-radio-button>
        </el-radio-group>
      </div>

      <template v-if="mainTab === 'detail'">
        <div v-for="group in cfg.metricGroups" :key="group.title" class="metric-block">
          <h3>{{ group.title }}</h3>
          <div class="metric-grid">
            <div class="metric-card" v-for="m in group.items" :key="m.key">
              <div class="m-label">{{ m.label }}</div>
              <div class="m-value">{{ formatNum(metrics[m.key]) }}</div>
              <div v-if="today[m.key] != null" class="m-today">今日 {{ formatDelta(today[m.key]) }}</div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="portrait-grid">
          <div class="portrait-card" v-for="block in portraitBlocks" :key="block.key">
            <h3>{{ block.title }} <small>近30日访客</small></h3>
            <ul>
              <li v-for="row in block.rows" :key="row.name">
                <span>{{ row.name }}</span>
                <em>{{ row.percent }}%</em>
              </li>
              <li v-if="!block.rows.length" class="empty">暂无数据</li>
            </ul>
          </div>
        </div>
      </template>
    </section>

    <section v-if="hasFans" class="panel">
      <div class="section-head"><h2>粉丝 / 关注者</h2></div>
      <div class="metric-grid fans">
        <div class="metric-card">
          <div class="m-label">总数</div>
          <div class="m-value">{{ formatNum(fans.followers || fans.fans_count || account.followers) }}</div>
        </div>
        <div class="metric-card">
          <div class="m-label">{{ cfg.fansDeltaLabel || '昨日变化' }}</div>
          <div class="m-value">{{ formatDelta(fans.yesterday_change || fans.new_fans) }}</div>
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="section-head"><h2>{{ cfg.topWorkLabel }} TOP <small>{{ topWorks.length }}</small></h2></div>
      <el-table v-if="topWorks.length" :data="topWorks" size="small" class="board-table">
        <el-table-column type="index" label="#" width="48" />
        <el-table-column prop="title" label="标题" min-width="220" show-overflow-tooltip />
        <el-table-column :prop="cfg.topWorkProp" :label="cfg.topWorkLabel" width="90" :formatter="cellNum" />
        <el-table-column label="发布" width="160">
          <template #default="{ row }">{{ formatBeijingDateTime(row.published_at) }}</template>
        </el-table-column>
      </el-table>
      <el-empty v-else description="暂无作品" :image-size="48" />
    </section>

    <section class="panel">
      <div class="section-head"><h2>作品列表 <small>{{ works.length }}</small></h2></div>
      <el-table :data="works" size="small" class="board-table" max-height="420" @row-click="openWork">
        <el-table-column prop="title" label="标题" min-width="220" show-overflow-tooltip />
        <el-table-column prop="note_type" label="类型" width="80" />
        <el-table-column
          v-for="col in cfg.workColumns"
          :key="col.prop"
          :prop="col.prop"
          :label="col.label"
          :width="col.width"
          :formatter="cellNum"
        />
        <el-table-column label="发布" width="160">
          <template #default="{ row }">{{ formatBeijingDateTime(row.published_at) }}</template>
        </el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { publishV2Api } from '../api'
import BoardCrumb from '../components/BoardCrumb.vue'
import {
  resolveGroupId,
  resolveGroupName,
  inheritBoardQuery,
  platformGroupLink,
  itemBoardLink,
} from '../utils/boardNav'
import { formatBeijingDateTime } from '../utils/dateTime'
import { PLATFORM_ACCOUNT_BOARD, resolveBoardPath } from '../utils/platformBoardConfig'

const props = defineProps({
  platform: { type: String, required: true },
})

const cfg = computed(() => PLATFORM_ACCOUNT_BOARD[props.platform])
const route = useRoute()
const router = useRouter()
const accountId = computed(() => String(route.params.accountId || ''))
const fromGroupId = computed(() => resolveGroupId(route.query))
const fromGroupName = computed(() => resolveGroupName(route.query, fromGroupId.value))
const days = ref(
  cfg.value.ranges.includes(Number(route.query.range))
    ? Number(route.query.range)
    : cfg.value.defaultRange,
)
const loading = ref(false)
const syncing = ref(false)
const loaded = ref(false)
const error = ref('')
const mainTab = ref('detail')
const payload = ref({})

const account = computed(() => payload.value.account || {})
const metrics = computed(() => payload.value.metrics || {})
const today = computed(() => metrics.value.today || {})
const fans = computed(() => payload.value.fans_overview || {})
const works = computed(() => {
  const list = payload.value.works || payload.value.notes
  if (Array.isArray(list)) return list
  return Array.isArray(list?.items) ? list.items : []
})
const statsError = computed(() => payload.value.stats_error || '')
const hasFans = computed(() => Object.keys(fans.value).length > 0 || account.value.followers != null)
const topWorks = computed(() => {
  const tops = payload.value.top_works
  const prop = cfg.value.topWorkProp
  if (Array.isArray(tops) && tops.length) return tops.slice(0, 3)
  return [...works.value]
    .sort((a, b) => Number(b?.[prop] || 0) - Number(a?.[prop] || 0))
    .slice(0, 3)
})
const portrait = computed(() => payload.value.visitor_portrait || {})
const fromCache = computed(() => !!payload.value.from_cache)
const dataSource = computed(() => payload.value.data_source || '')
const fetchedAt = computed(() => payload.value.fetched_at || '')
const periodLabel = computed(() => payload.value.period?.label || rangeLabel(days.value))
const cacheTitle = computed(() => {
  const day = payload.value.cache_day
  return day ? `本地快照（${day}），点「同步数据」更新` : '本地快照，点「同步数据」更新'
})
const portraitBlocks = computed(() => [
  { key: 'source', title: '流量来源', rows: portrait.value.source || [] },
  { key: 'gender', title: '性别分布', rows: portrait.value.gender || [] },
  { key: 'age', title: '年龄分布', rows: portrait.value.age || [] },
  { key: 'location', title: '地域分布', rows: (portrait.value.location || []).slice(0, 8) },
])

const formatNum = (v) => {
  const n = Number(v || 0)
  if (!Number.isFinite(n)) return '0'
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(1)}万`
  return String(Math.round(n))
}
const formatDelta = (v) => {
  const n = Number(v || 0)
  if (n > 0) return `↑ ${formatNum(n)}`
  if (n < 0) return `↓ ${formatNum(Math.abs(n))}`
  return '0'
}
const formatTime = (value) => formatBeijingDateTime(value, '')
const cellNum = (_r, _c, v) => formatNum(v)
const kpiValue = (k) => resolveBoardPath(payload.value, k.path, k.alt)
const rangeLabel = (d) => cfg.value.rangeLabels?.[d] || (d === 0 ? '累计' : `近${d}天`)

async function loadBoard(sync = false) {
  if (sync) syncing.value = true
  else loading.value = true
  error.value = ''
  try {
    const data = await cfg.value.fetchBoard(accountId.value, { days: days.value, sync })
    payload.value = cfg.value.normalize
      ? cfg.value.normalize(data || {}, { days: days.value })
      : (data || {})
    loaded.value = true
    const uid = data?.account?.external_user_id
    if (uid && uid !== accountId.value && String(accountId.value).startsWith('mp_')) {
      router.replace({
        path: `/data-analytics/${cfg.value.key}/${encodeURIComponent(uid)}`,
        query: { ...route.query, range: String(days.value) },
      })
    }
  } catch (e) {
    error.value = e?.response?.data?.detail || e?.message || '加载失败'
  } finally {
    loading.value = false
    syncing.value = false
  }
}

function setDays(d) {
  days.value = d
  router.replace({ query: { ...route.query, range: String(d) } })
  loadBoard(false)
}
function syncBoard() {
  loadBoard(true)
}
function backToPlatGroup() {
  if (!fromGroupId.value) return
  router.push(platformGroupLink(cfg.value.key, fromGroupId.value, days.value))
}
function openWork(row) {
  const field = cfg.value.workIdField
  if (!field) return
  const id = row?.[field] || row?.note_id || row?.item_id || row?.id
  if (!id) return
  router.push(itemBoardLink(cfg.value.key, accountId.value, id, inheritBoardQuery(route, { range: String(days.value) })))
}

onMounted(() => loadBoard(false))
</script>

<style scoped>
.plat-account-board { color: var(--mp-text); min-height: calc(100vh - 48px); padding: 4px 0 32px; }
.board-top { display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 12px; }
.board-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.range-switch { display: inline-flex; background: #f3f5f8; border-radius: 999px; padding: 3px; }
.range-switch button { border: 0; background: transparent; padding: 6px 12px; border-radius: 999px; cursor: pointer; color: var(--mp-text-muted); font: inherit; }
.range-switch button.on { background: #fff; color: var(--mp-accent); box-shadow: 0 1px 4px rgba(52,65,93,0.08); }
.board-alert { margin-bottom: 12px; }
.hero {
  display: grid; grid-template-columns: 1.2fr 1fr; gap: 14px; padding: 16px; border-radius: 14px;
  background: var(--mp-surface); border: 1px solid var(--mp-border); margin-bottom: 14px;
}
.hero-identity { display: flex; gap: 14px; align-items: center; }
.hero h1 { margin: 0; font-size: 24px; font-weight: 800; }
.hero p { margin: 6px 0 0; color: var(--mp-text-muted); font-size: 13px; }
.hero-kpis { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.hk { padding: 12px; border-radius: 12px; background: #f6f8fb; }
.hk span { display: block; font-size: 12px; color: var(--mp-text-muted); }
.hk strong { font-size: 22px; }
.mono { font-family: ui-monospace, monospace; font-size: 12px; }
.panel { background: var(--mp-surface); border: 1px solid var(--mp-border); border-radius: 14px; padding: 16px; margin-bottom: 14px; }
.section-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; gap: 12px; flex-wrap: wrap; }
.section-head h2 { margin: 0; font-size: 16px; }
.section-head small { color: var(--mp-text-muted); font-weight: 400; margin-left: 6px; }
.metric-block { margin-bottom: 18px; }
.metric-block h3 { margin: 0 0 10px; font-size: 14px; color: var(--mp-text-secondary); }
.metric-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; }
.metric-card { background: #f6f8fb; border-radius: 12px; padding: 12px 14px; border: 1px solid var(--mp-border); }
.m-label { font-size: 12px; color: var(--mp-text-muted); }
.m-value { font-size: 22px; font-weight: 650; margin-top: 4px; }
.m-today { font-size: 12px; color: var(--mp-accent); margin-top: 6px; }
.portrait-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.portrait-card { background: #f6f8fb; border-radius: 12px; padding: 14px; border: 1px solid var(--mp-border); }
.portrait-card h3 { margin: 0 0 10px; font-size: 14px; }
.portrait-card small { color: var(--mp-text-muted); font-weight: 400; }
.portrait-card ul { list-style: none; margin: 0; padding: 0; }
.portrait-card li { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid var(--mp-border); font-size: 13px; }
.portrait-card li.empty { color: var(--mp-text-muted); border: 0; }
.board-table { width: 100%; cursor: pointer; }
@media (max-width: 900px) {
  .hero, .portrait-grid { grid-template-columns: 1fr; }
}
</style>
